import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Header, MobileNav } from '../../components/Header';
import { ProviderCard } from '../../components/ProviderCard';
import { ProvidersMap } from '../../components/ProvidersMap';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Slider } from '../../components/ui/slider';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../../components/ui/sheet';
import { providersAPI } from '../../lib/api';
import { SERVICE_TYPES, CITIES, getCityCoords } from '../../lib/constants';
import { MagnifyingGlass, Funnel, MapTrifold, List, Dog, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

export default function ProvidersListPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list');
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCity, setSelectedCity] = useState(user?.city || '');
  const [selectedService, setSelectedService] = useState(searchParams.get('service') || '');
  const [minRating, setMinRating] = useState(0);
  const [maxPrice, setMaxPrice] = useState(100);

  const cityCoords = getCityCoords(selectedCity || user?.city);

  useEffect(() => {
    const fetchProviders = async () => {
      setLoading(true);
      try {
        const params = {};
        if (selectedCity) params.city = selectedCity;
        if (selectedService) params.service_type = selectedService;
        if (minRating > 0) params.min_rating = minRating;
        if (maxPrice < 100) params.max_price = maxPrice;
        
        const data = await providersAPI.getProviders(params);
        setProviders(data);
      } catch (error) {
        toast.error('Erro ao carregar prestadores');
      } finally {
        setLoading(false);
      }
    };

    fetchProviders();
  }, [selectedCity, selectedService, minRating, maxPrice]);

  const filteredProviders = providers.filter(p => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      p.user?.name?.toLowerCase().includes(query) ||
      p.bio?.toLowerCase().includes(query)
    );
  });

  const clearFilters = () => {
    setSelectedCity('');
    setSelectedService('');
    setMinRating(0);
    setMaxPrice(100);
    setSearchParams({});
  };

  const hasActiveFilters = selectedCity || selectedService || minRating > 0 || maxPrice < 100;

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Header />
      
      <main className="container mx-auto px-4 py-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-2xl font-bold font-outfit mb-6">Prestadores</h1>

          {/* Search and Filters Bar */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <MagnifyingGlass className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                placeholder="Pesquisar por nome..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-12 rounded-full"
                data-testid="search-input"
              />
            </div>
            
            <div className="flex gap-2">
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline" className="h-12 rounded-full px-6" data-testid="filters-btn">
                    <Funnel className="w-5 h-5 mr-2" />
                    Filtros
                    {hasActiveFilters && (
                      <span className="ml-2 w-2 h-2 rounded-full bg-primary" />
                    )}
                  </Button>
                </SheetTrigger>
                <SheetContent>
                  <SheetHeader>
                    <SheetTitle>Filtros</SheetTitle>
                  </SheetHeader>
                  <div className="mt-6 space-y-6">
                    <div>
                      <label className="text-sm font-medium mb-2 block">Cidade</label>
                      <Select value={selectedCity} onValueChange={setSelectedCity}>
                        <SelectTrigger data-testid="filter-city">
                          <SelectValue placeholder="Todas as cidades" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="">Todas as cidades</SelectItem>
                          {CITIES.map((city) => (
                            <SelectItem key={city.id} value={city.id}>
                              {city.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="text-sm font-medium mb-2 block">Tipo de Serviço</label>
                      <Select value={selectedService} onValueChange={setSelectedService}>
                        <SelectTrigger data-testid="filter-service">
                          <SelectValue placeholder="Todos os serviços" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="">Todos os serviços</SelectItem>
                          {SERVICE_TYPES.map((service) => (
                            <SelectItem key={service.id} value={service.id}>
                              {service.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="text-sm font-medium mb-2 block">
                        Avaliação Mínima: {minRating} estrelas
                      </label>
                      <Slider
                        value={[minRating]}
                        onValueChange={([v]) => setMinRating(v)}
                        max={5}
                        step={0.5}
                        className="py-4"
                      />
                    </div>

                    <div>
                      <label className="text-sm font-medium mb-2 block">
                        Preço Máximo: {maxPrice}€/h
                      </label>
                      <Slider
                        value={[maxPrice]}
                        onValueChange={([v]) => setMaxPrice(v)}
                        max={100}
                        step={5}
                        className="py-4"
                      />
                    </div>

                    {hasActiveFilters && (
                      <Button
                        variant="outline"
                        onClick={clearFilters}
                        className="w-full"
                        data-testid="clear-filters"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Limpar Filtros
                      </Button>
                    )}
                  </div>
                </SheetContent>
              </Sheet>

              <div className="flex items-center gap-1 border rounded-full p-1">
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className="rounded-full"
                  data-testid="view-list"
                >
                  <List className="w-4 h-4" />
                </Button>
                <Button
                  variant={viewMode === 'map' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('map')}
                  className="rounded-full"
                  data-testid="view-map"
                >
                  <MapTrifold className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Active Filters */}
          {hasActiveFilters && (
            <div className="flex flex-wrap gap-2 mb-4">
              {selectedCity && (
                <span className="filter-chip active">
                  {CITIES.find(c => c.id === selectedCity)?.name}
                  <button onClick={() => setSelectedCity('')}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {selectedService && (
                <span className="filter-chip active">
                  {SERVICE_TYPES.find(s => s.id === selectedService)?.name}
                  <button onClick={() => setSelectedService('')}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
            </div>
          )}

          {/* Results */}
          {(() => {
            if (loading) {
              return (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <Card key={i} className="h-64 animate-pulse bg-muted" />
                  ))}
                </div>
              );
            }
            if (viewMode === 'map') {
              return (
                <ProvidersMap
                  providers={filteredProviders}
                  center={[cityCoords.lat, cityCoords.lng]}
                  zoom={12}
                  height="calc(100vh - 250px)"
                />
              );
            }
            return (
              <>
                <p className="text-sm text-muted-foreground mb-4">
                  {filteredProviders.length} prestador{filteredProviders.length !== 1 ? 'es' : ''} encontrado{filteredProviders.length !== 1 ? 's' : ''}
                </p>
                
                {filteredProviders.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <Dog weight="duotone" className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="font-semibold mb-2">Nenhum prestador encontrado</h3>
                      <p className="text-muted-foreground text-sm mb-4">
                        Tente ajustar os filtros para ver mais resultados
                      </p>
                      {hasActiveFilters && (
                        <Button variant="outline" onClick={clearFilters}>
                          Limpar Filtros
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredProviders.map((provider) => (
                      <ProviderCard key={provider.provider_id} provider={provider} />
                    ))}
                  </div>
                )}
              </>
            );
          })()}
        </motion.div>
      </main>

      <MobileNav />
    </div>
  );
}
